import "@/styles/Layout.scss";
import { getUser } from "@/hooks/useLocalStorage";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeftIcon } from "@radix-ui/react-icons";
import { useLocation, useNavigate } from "react-router-dom";
import Spinner from "@/components/Spinner";
import Gallery from "@/components/Gallery";
import ProductAction from "@/components/ProductAction";

function Product() {
  const user = getUser();
  const navigate = useNavigate();
  const location = useLocation();

  // Safely destructure and provide fallback default values
  const { data } = location.state || {};
  const product = data?.product || {}; // Default to empty object if data.product is missing

  // Initialize the form with the product
  const form = useForm({
    defaultValues: {
      grade: product.grade || "",
      form: product.form || "",
      price: `${product?.price} ${product?.priceUnit}` || "",
      quantity: `${product?.quantity} ${product?.quantityUnit}` || "",
      location: product.location || "",
      source: product.source || "",
      postalCode: product.postalCode || "",
      priceUnit: product.priceUnit || "",
      description: product.description || "",
      quantityUnit: product.quantityUnit || "",
    },
  });

  if (!data) {
    return <Spinner />;
  }

  return (
    <div className="sectionWrapper mobileWrapper">
      <div
        onClick={() => navigate(-1)}
        className="flex items-center mb-1 cursor-pointer"
      >
        <ArrowLeftIcon style={{ transform: "scale(1.1)" }} />
        <h1 className="text-sm text-muted ml-1">Back</h1>
      </div>

      <div>
        <h1 className="text-sm font-bold text-green ml-0.5">Product Form</h1>
        <div className="highlight highlight-lg" />
      </div>

      <p className="mt-3 ml-3 text-sm text-muted">
        Explore all the details about the product on this page.
      </p>

      <Form {...form}>
        <form className="space-y-4 p-3">
          <div className="flex justify-between">
            {/* Grade */}
            <div className="w-[49%]">
              <FormField
                control={form.control}
                name="grade"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Grade of Material</FormLabel>
                    <FormControl className="bg-white">
                      <Input type="text" {...field} disabled />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Form */}
            <div className="w-[49%]">
              <FormField
                control={form.control}
                name="form"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Form of Material</FormLabel>
                    <FormControl className="bg-white">
                      <Input type="text" {...field} disabled />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className="flex justify-between">
            {/* Price */}
            {product.price && (
              <div className="w-[49%]">
                <FormField
                  control={form.control}
                  name="price"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Price</FormLabel>
                      <FormControl className="bg-white">
                        <Input type="text" {...field} disabled />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {/* Quantity */}
            {product.quantity && (
              <div className="w-[49%]">
                <FormField
                  control={form.control}
                  name="quantity"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Quantity</FormLabel>
                      <FormControl className="bg-white">
                        <Input type="text" {...field} disabled />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}
          </div>

          <div className="flex justify-between">
            {/* Postal Code */}
            {product.postalCode && (
              <div className="w-[49%]">
                <FormField
                  control={form.control}
                  name="postalCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Postal Code</FormLabel>
                      <FormControl className="bg-white">
                        <Input type="text" {...field} disabled />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {/* Location */}
            {product.location && (
              <div className="w-[49%]">
                <FormField
                  control={form.control}
                  name="location"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Location</FormLabel>
                      <FormControl className="bg-white">
                        <Input type="text" {...field} disabled />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}
          </div>

          {/* Description */}
          {product.description && (
            <div className="flex justify-between">
              <div className="w-[100%]">
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea {...field} disabled />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          )}

          {/* Source of inquiry */}
          {product.source && (
            <div className="flex justify-between">
              <div className="w-[100%]">
                <FormField
                  control={form.control}
                  name="source"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Source of inquiry</FormLabel>
                      <FormControl>
                        <Input {...field} disabled />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          )}

          {/* Product Images */}
          <div className="mt-2">
            <FormLabel>Product Images</FormLabel>
            <Gallery
              user={user}
              allImages={product.images}
              requestId={data.request_id}
              referenceId={data.reference_id}
            />
          </div>

          {/* Product Action - Buyer/Agents */}
          <ProductAction user={user} item={data} />
        </form>
      </Form>
    </div>
  );
}

export default Product;
